import { useState } from 'react';
import { ChevronDown, Calendar, Users, FileText, CreditCard, Clock, Settings, Zap, HelpCircle, BookOpen } from 'lucide-react';
import { cn } from '../lib/utils';

const faqs = [
  {
    category: 'Getting Started',
    icon: Zap,
    questions: [
      {
        q: 'How do I create my first booking?',
        a: 'Navigate to the Calendar page and click on any time slot, or use the "New Booking" button in the top right. Select a client, service, date/time, and duration. You can also drag across multiple hour slots to quickly set a longer duration.'
      },
      {
        q: 'How do I add a new client?',
        a: 'Go to Clients in the sidebar and click "New Client". Fill in their details including name, email, phone, and choose between Individual or Business client type. For business clients, you can add a company name which will be displayed on bookings and invoices.'
      },
      {
        q: 'How do I set up my services?',
        a: 'Navigate to Services and click "Add Service". Enter the service name, default duration (in minutes), and hourly/session rate. You can also choose a color to help identify different services on your calendar.'
      }
    ]
  },
  {
    category: 'Calendar',
    icon: Calendar,
    questions: [
      {
        q: 'What do the different colored slots mean?',
        a: 'White/clear slots are within your working hours. Grey slots are outside your configured working hours (weekends, early mornings, late evenings). Red/pink slots are blocked times (lunch breaks, holidays, etc.). You can still book on grey or blocked slots if needed - you\'ll just see a confirmation warning.'
      },
      {
        q: 'How do I edit an existing booking?',
        a: 'Click on any booking card to see the context menu, then select "Edit Booking". This will open a form with all the booking details pre-filled. Make your changes and click "Save Changes".'
      },
      {
        q: 'How do I block out time for lunch or personal appointments?',
        a: 'Click on any time slot to open the context menu, then select "Block Time". Enter a title (e.g., "Lunch Break"), set the duration, and optionally check "Repeat every [day]" for recurring blocks. Blocked times appear with a pink background and the title displayed inside.'
      },
      {
        q: 'What does the pink "PREPAID" badge mean?',
        a: 'The PREPAID badge indicates that this booking has been paid for in advance through a prepayment invoice. The client has credit on their account that covers this session.'
      },
      {
        q: 'How do I book a multi-hour session?',
        a: 'You can either: 1) Click and drag across multiple hour slots on the calendar to auto-select the duration, or 2) Click any slot and use the Duration dropdown in the booking form to select from 30 minutes to 8 hours.'
      },
      {
        q: 'Can I drag and drop bookings to reschedule?',
        a: 'Yes! Simply drag any scheduled booking to a new time slot. If you drag to a blocked or non-working time, you\'ll see a confirmation dialog asking if you want to proceed.'
      }
    ]
  },
  {
    category: 'Clients',
    icon: Users,
    questions: [
      {
        q: 'What\'s the difference between Individual and Business clients?',
        a: 'Individual clients are private persons - their name appears on bookings and invoices. Business clients have a company name that appears instead of the contact person\'s name on the calendar and invoices. You can also add ABN/Tax ID for business clients.'
      },
      {
        q: 'How do I view a client\'s transaction history?',
        a: 'Click on any client in the Clients list to open their detail panel. You\'ll see their complete transaction history including bookings, invoices, payments, and credits. Use the date range filter to narrow down the view, and export to CSV or print for your records.'
      },
      {
        q: 'What does a negative balance mean?',
        a: 'A negative balance (shown in red) means the client owes you money - they have unpaid invoices. A positive balance (shown in green) means the client has credit on their account from prepayments or overpayments.'
      }
    ]
  },
  {
    category: 'Invoicing',
    icon: FileText,
    questions: [
      {
        q: 'What are the different invoice columns/stages?',
        a: `The invoice board has 6 columns:
• Upcoming Sessions - Scheduled future bookings
• Completed Sessions - Sessions ready to be invoiced  
• Draft Invoices - Created but not yet sent to client
• Outstanding Amount - Sent invoices awaiting payment
• Pre Paid / Credit - Client prepayments and credits
• Paid Invoices - Fully paid invoices`
      },
      {
        q: 'How do I create an invoice?',
        a: 'Select completed sessions in the "Completed Sessions" column and click "Create Invoice", or drag them to the "Draft Invoices" column. You can also create a manual invoice from the "New Invoice" button for custom charges or prepayments.'
      },
      {
        q: 'What is a Draft invoice?',
        a: 'A Draft invoice has been created but not sent to the client. You can add more sessions to a draft, edit line items, or delete it. Once you\'re ready, click "Send" to mark it as sent and move it to Outstanding.'
      },
      {
        q: 'How do I handle partial payments?',
        a: 'When recording a payment, simply enter the amount received. If it\'s less than the invoice total, the remaining balance will stay in Outstanding. The invoice will show "Partially Paid" with the remaining amount due.'
      },
      {
        q: 'What happens if a client overpays?',
        a: 'If a payment exceeds the invoice amount, the invoice is marked as Paid and the excess amount is automatically added as a credit to the client\'s account. You\'ll see this credit in the "Pre Paid / Credit" column.'
      },
      {
        q: 'How do I apply client credit to an invoice?',
        a: 'When viewing an invoice with an outstanding balance, if the client has available credit, you\'ll see an option to apply credit. Select the amount to apply and it will reduce the invoice balance accordingly.'
      }
    ]
  },
  {
    category: 'Payments',
    icon: CreditCard,
    questions: [
      {
        q: 'What payment methods can I record?',
        a: 'You can record payments via Cash, Bank Transfer, Credit Card, Debit Card, or PayPal. Select the appropriate method when recording a payment to keep accurate records of how you were paid.'
      },
      {
        q: 'How do I create a prepayment/deposit?',
        a: 'Create a new manual invoice and check the "This is a prepayment" option. Once paid, the amount becomes a credit on the client\'s account that can be applied to future invoices or sessions.'
      }
    ]
  },
  {
    category: 'Settings & Configuration',
    icon: Settings,
    questions: [
      {
        q: 'How do I set my working hours?',
        a: 'Go to Settings and find the Availability section. Set your start and end times for each day of the week. Days outside these hours will appear grey on the calendar.'
      },
      {
        q: 'What are the statement fields used for?',
        a: 'The statement fields (Business Address, Phone, GST/Tax Number) appear on exported client statements and can be used for professional invoice headers. Configure these in Settings under the Statement Settings section.'
      },
      {
        q: 'How do I set up auto-invoicing for a client?',
        a: 'Edit a client and change their Invoice Mode from "Manual" to "Auto". Then select the frequency (Weekly, Fortnightly, Monthly, or Last Day of Month), the day, and the time when invoices should be automatically generated.'
      }
    ]
  }
];

const tips = [
  {
    title: 'Quick Duration Selection',
    description: 'Click and drag across multiple hours on the calendar to automatically set the booking duration.'
  },
  {
    title: 'Business Client Display',
    description: 'For business clients, the company name is shown on the calendar and invoices instead of the contact person\'s name.'
  },
  {
    title: 'Right-Click Context Menu',
    description: 'Click on any calendar slot to access quick options for New Booking or Block Time.'
  },
  {
    title: 'Keyboard Navigation',
    description: 'Use the left/right arrows in the calendar header to navigate between weeks, or click "Today" to jump to the current week.'
  },
  {
    title: 'Other Service Option',
    description: 'Use "Other (Custom)" as a service for one-off bookings that don\'t fit your regular services. Note: You\'ll need to create a manual invoice for these.'
  },
  {
    title: 'Recurring Blocks',
    description: 'When blocking time, check the "Repeat" option to automatically block the same time slot every week (e.g., weekly lunch breaks).'
  }
];

function AccordionItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="border-b border-border last:border-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 text-left hover:bg-muted/50 px-4 -mx-4 transition-colors"
      >
        <span className="font-medium text-sm">{question}</span>
        <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", isOpen && "rotate-180")} />
      </button>
      {isOpen && (
        <div className="pb-4 text-sm text-muted-foreground whitespace-pre-line">
          {answer}
        </div>
      )}
    </div>
  );
}

function FAQSection({ category, icon: Icon, questions }) {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="flex items-center gap-3 p-4 bg-muted/30 border-b border-border">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <h3 className="font-semibold">{category}</h3>
      </div>
      <div className="p-4">
        {questions.map((item, idx) => (
          <AccordionItem
            key={idx}
            question={item.q}
            answer={item.a}
            isOpen={openIndex === idx}
            onToggle={() => setOpenIndex(openIndex === idx ? null : idx)}
          />
        ))}
      </div>
    </div>
  );
}

export default function HelpPage() {
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-6 lg:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <HelpCircle className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-3xl font-heading font-bold">Help & FAQ</h1>
          </div>
          <p className="text-muted-foreground">
            Everything you need to know about using Bōkr to manage your scheduling and invoicing.
          </p>
        </div>

        {/* Quick Tips */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold">Quick Tips</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tips.map((tip, idx) => (
              <div key={idx} className="bg-card rounded-lg border border-border p-4 hover:shadow-md transition-shadow">
                <h4 className="font-medium text-sm mb-1">{tip.title}</h4>
                <p className="text-xs text-muted-foreground">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold">Frequently Asked Questions</h2>
          </div>
          {faqs.map((section, idx) => (
            <FAQSection key={idx} {...section} />
          ))}
        </div>

        {/* Contact Support */}
        <div className="mt-10 p-6 bg-muted/30 rounded-xl border border-border text-center">
          <h3 className="font-semibold mb-2">Still need help?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Can't find what you're looking for? We're here to help.
          </p>
          <p className="text-sm text-muted-foreground">
            Contact support at <span className="text-primary font-medium">support@getbokr.com</span>
          </p>
        </div>
      </div>
    </div>
  );
}
